import React, { type ComponentPropsWithoutRef, useMemo } from 'react';
import { ErrorFlag } from '@atlassian/jira-flags';
import { useHelpPanelLink } from '@atlassian/jira-help-panel-button/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { BulkOpsFailureReason } from '../../../state/actions/bulk-ops-flags/error/index.tsx';
import { BULK_EDIT_FAILURE_IPH_ARTICLE_ID } from './constants.tsx';
import messages from './messages.tsx';

type FlagActions = ComponentPropsWithoutRef<typeof ErrorFlag>['actions'];

export type BulkActionsFailedFlagProps = {
	failedCount: number;
	failureReason: BulkOpsFailureReason;
	totalCount: number;
	onDismissed?: (id: string | number) => void;
	id: string | number;
	onRetry: () => void;
	onSelectFailedItems: () => void;
	onViewSummary: () => void;
	showViewSummaryButton: boolean;
	showRetryButton: boolean;
	showLearnMoreButton: boolean;
	showSelectFailedItemsButton: boolean;
};

export default function BulkActionsFailedFlag({
	failedCount,
	id: flagId,
	onRetry,
	onViewSummary,
	totalCount,
	onDismissed,
	showRetryButton,
	showViewSummaryButton,
	showSelectFailedItemsButton,
	showLearnMoreButton,
	failureReason,
	onSelectFailedItems,
}: BulkActionsFailedFlagProps) {
	const { formatMessage } = useIntl();

	const openInProductHelp = useHelpPanelLink();

	const didAllUpdatesFail = failedCount === totalCount;

	const actions: FlagActions = useMemo(() => {
		const flagActions: FlagActions = [];

		if (showSelectFailedItemsButton) {
			flagActions.push({
				content: formatMessage(messages.selectFailedActionContent),
				onClick: onSelectFailedItems,
			});
		}

		if (showRetryButton) {
			flagActions.push({
				content: formatMessage(messages.retryActionContent),
				onClick: onRetry,
			});
		}

		if (showViewSummaryButton) {
			flagActions.push({
				content: formatMessage(messages.viewSummaryActionContent),
				onClick: onViewSummary,
			});
		}

		if (showLearnMoreButton) {
			flagActions.push({
				content: formatMessage(messages.learnMoreActionContent),
				onClick: () => openInProductHelp?.(BULK_EDIT_FAILURE_IPH_ARTICLE_ID),
			});
		}

		return flagActions;
	}, [
		showRetryButton,
		showViewSummaryButton,
		formatMessage,
		onRetry,
		onViewSummary,
		onSelectFailedItems,
		showSelectFailedItemsButton,
		openInProductHelp,
		showLearnMoreButton,
	]);

	const title = didAllUpdatesFail
		? formatMessage(messages.flagTitleError)
		: formatMessage(messages.flagTitlePartialError, {
				failedCount,
				totalCount,
			});

	const description = showRetryButton
		? formatMessage(messages.flagDescriptionError)
		: formatMessage(messages.flagDescriptionErrorTryLater);

	const validationErrorDescription = formatMessage(messages.flagDescriptionValidationError);
	const networkErrorDescriptionTryLater = formatMessage(messages.flagDescriptionNetworkError);

	const descriptionMap: Record<BulkOpsFailureReason, string> = {
		network: showRetryButton ? description : networkErrorDescriptionTryLater,
		validation: validationErrorDescription,
		server: description,
		mixed: description,
	};

	return (
		<ErrorFlag
			id={flagId}
			onDismissed={onDismissed}
			title={title}
			description={descriptionMap[failureReason]}
			actions={actions}
			testId="software-backlog.flags.bulk-actions.error.bulk-actions-failed-flag"
		/>
	);
}
